import Input from "common/Inputs/Input";
import Form from "common/Form/Form";
import { LanguageNameSpaces, RequestSource } from "@shared/types";
import { AuthDescription, PrimaryTitle } from "features/auth/styles";
import { useTheme } from "styled-components";
import { ArrowLeft } from "lucide-react";
import React, { ChangeEvent, useState } from "react";
import { useCaptcha } from "utils/hooks/useCaptcha";
import useMedia from "utils/hooks/useMedia";
import { LOGIN_PATH, MOBILE_SEARCH } from "common/constants";
import {
  EMAIL_INPUT_PROPS,
  getEmailFormItemRules,
} from "features/auth/constants";
import Text from "common/Typography/Text";
import ResendEmailInstructions from "features/auth/ResendEmailInstructions";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import Flex from "common/Boxes/Flex";
import Button, { StyledDefaultButton } from "common/Buttons/Button";
import Link from "common/Typography/Link";
import { sharedLib } from "@shared/services/shared.service";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  AUTH_BACK_TO_LOGIN_BUTTON_ID,
  AUTH_FORGOT_PASSWORD_EMAIL_INPUT_ID,
  AUTH_FORGOT_PASSWORD_SUBMIT_BUTTON_ID,
} from "testIds.ts";

const ForgotPasswordForm = () => {
  const { isWhitelabel } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { historyPush } = useHistoryWithQuery();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();
  const {
    display: displayResultMessage,
    error,
    success,
  } = useAuthResultMessage({ inlineSuccess: true });

  const [email, setEmail] = useState<string>();
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [showSupportLink, setShowSupportLink] = useState(false);

  const [forgotPassword, { isLoading: isForgotPasswordLoading }] =
    sharedLib.sharedApi.authentication.useForgotPasswordMutation();

  const handleForgotPassword = (captcha_token?: string) => {
    if (email) {
      displayResultMessage(
        forgotPassword({
          email,
          request_source: RequestSource.WEBSITE,
          captcha_token,
          is_whitelabel: isWhitelabel,
        }).unwrap(),
        t("forgot_password.email_sent"),
        t("forgot_password.failed"),
        6
      ).catch(() => {});
    }
    setShowResendEmail(true);
    if (showResendEmail) setShowSupportLink(true);
  };

  const {
    verify: onFinish,
    isLoading: isCaptchaLoading,
    captcha,
  } = useCaptcha({
    onSuccess: (captcha_token) => {
      handleForgotPassword(captcha_token);
    },
  });

  const isLoading = isForgotPasswordLoading || isCaptchaLoading;

  return (
    <Form
      name="login"
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      layout="vertical"
      requiredMark={false}
      style={{ textAlign: isNewLook ? "start" : "center" }}
    >
      {isNewLook ? (
        <>
          <PrimaryTitle $isMobile={isMobile}>
            {t("forgot_password.title_new_look")}
          </PrimaryTitle>
          <AuthDescription>{t("forgot_password.description")}</AuthDescription>
        </>
      ) : (
        <>
          <PrimaryTitle $isMobile={isMobile} style={{ marginBottom: 16 }}>
            {t("forgot_password.title")}
          </PrimaryTitle>
          <Text style={{ marginBottom: 24 }}>
            {t("forgot_password.description")}
          </Text>
        </>
      )}
      <Form.Item
        label={t("fields.email")}
        name="email"
        rules={getEmailFormItemRules(t)}
        style={{ textAlign: "start" }}
      >
        <Input
          size="large"
          data-testid={AUTH_FORGOT_PASSWORD_EMAIL_INPUT_ID}
          {...EMAIL_INPUT_PROPS}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
      </Form.Item>
      {isNewLook ? (
        <Flex $flexDirection="column" style={{ width: "100%", rowGap: 12 }}>
          <AuthInlineError message={error} style={{ margin: 0 }} />
          <AuthInlineError
            message={success}
            type="success"
            style={{ margin: 0 }}
          />
          <Button
            type="primary"
            $size="l"
            htmlType="submit"
            loading={isLoading}
            disabled={!email}
            data-testid={AUTH_FORGOT_PASSWORD_SUBMIT_BUTTON_ID}
            style={{ width: "100%", marginTop: 8 }}
          >
            {t("forgot_password.reset_password_new_look")}
          </Button>
          <Button
            type="link"
            $size="l"
            icon={<ArrowLeft size={16} strokeWidth={1.75} aria-hidden />}
            onClick={() => historyPush(LOGIN_PATH)}
            data-testid={AUTH_BACK_TO_LOGIN_BUTTON_ID}
            style={{ width: "100%" }}
          >
            {t("forgot_password.back_to_login_new_look")}
          </Button>
        </Flex>
      ) : (
        <Flex
          $justifyContent="center"
          style={{ width: "100%", columnGap: 16, marginTop: 16 }}
        >
          <StyledDefaultButton
            $size="l"
            onClick={() => historyPush(LOGIN_PATH)}
            data-testid={AUTH_BACK_TO_LOGIN_BUTTON_ID}
            style={{ width: "100%" }}
          >
            {t("forgot_password.back_to_login")}
          </StyledDefaultButton>
          <Button
            type="primary"
            $size="l"
            htmlType="submit"
            loading={isLoading}
            disabled={!email}
            data-testid={AUTH_FORGOT_PASSWORD_SUBMIT_BUTTON_ID}
            style={{ width: "100%" }}
          >
            {t("forgot_password.reset_password")}
          </Button>
        </Flex>
      )}
      {showResendEmail && (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <Link onClick={onFinish}>
            {t(
              isNewLook
                ? "verify_email.resend_email_new_look"
                : "verify_email.resend_email"
            )}
          </Link>
        </div>
      )}
      {showSupportLink && <ResendEmailInstructions addBorder={true} />}
      {captcha}
    </Form>
  );
};

export default ForgotPasswordForm;
